import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { inquiryService, clientService, proposalService, engagementService, blogService } from '../services/api';

export default function DashboardPage() {
  const [stats, setStats] = useState({
    inquiries: { total: 0, new: 0 },
    clients: { total: 0 },
    proposals: { total: 0, pending: 0 },
    engagements: { total: 0, active: 0 },
    posts: { total: 0, published: 0 },
  });
  const [recentInquiries, setRecentInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load all data in parallel
      const [inquiries, clients, proposals, engagements, posts] = await Promise.all([
        inquiryService.getAll().catch(() => []),
        clientService.getAll().catch(() => []),
        proposalService.getAll().catch(() => []),
        engagementService.getAll().catch(() => []),
        blogService.getAll().catch(() => []),
      ]);

      setStats({
        inquiries: {
          total: inquiries.length,
          new: inquiries.filter(i => i.status === 'new').length,
        },
        clients: {
          total: clients.length,
        },
        proposals: {
          total: proposals.length,
          pending: proposals.filter(p => p.status === 'pending').length,
        },
        engagements: {
          total: engagements.length,
          active: engagements.filter(e => e.status === 'active').length,
        },
        posts: {
          total: posts.length,
          published: posts.filter(p => p.status === 'published').length,
        },
      });

      // Get 5 most recent inquiries
      setRecentInquiries(inquiries.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'New Inquiries', value: stats.inquiries.new, total: stats.inquiries.total, link: '/inquiries', color: 'bg-blue-500' },
    { label: 'Clients', value: stats.clients.total, link: '/clients', color: 'bg-green-500' },
    { label: 'Pending Proposals', value: stats.proposals.pending, total: stats.proposals.total, link: '/proposals', color: 'bg-yellow-500' },
    { label: 'Active Engagements', value: stats.engagements.active, total: stats.engagements.total, link: '/engagements', color: 'bg-purple-500' },
    { label: 'Published Posts', value: stats.posts.published, total: stats.posts.total, link: '/blog', color: 'bg-accent' },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard | CR Hood Admin</title>
      </Helmet>

      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {statCards.map((stat, index) => (
            <Link key={index} to={stat.link} className="card hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <span className="text-2xl font-bold text-white">{stat.value}</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  {stat.total !== undefined && (
                    <p className="text-xs text-gray-400">of {stat.total} total</p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Recent Inquiries */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Recent Inquiries</h2>
            <Link to="/inquiries" className="text-sm text-accent hover:underline">
              View All →
            </Link>
          </div>

          {recentInquiries.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No inquiries yet</p>
          ) : (
            <div className="divide-y">
              {recentInquiries.map((inquiry) => (
                <Link
                  key={inquiry.id}
                  to={`/inquiries/${inquiry.id}`}
                  className="flex items-center justify-between py-3 hover:bg-gray-50 -mx-6 px-6"
                >
                  <div>
                    <p className="font-medium text-gray-900">{inquiry.name}</p>
                    <p className="text-sm text-gray-500">{inquiry.company || inquiry.email}</p>
                  </div>
                  <span className={`badge ${inquiry.status === 'new' ? 'badge-info' : 'badge-gray'}`}>
                    {inquiry.status}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/blog/new" className="btn btn-primary">
              New Blog Post
            </Link>
            <Link to="/clients" className="btn btn-secondary">
              Add Client
            </Link>
            <Link to="/proposals/new" className="btn btn-secondary">
              Create Proposal
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
